import React, { memo, useCallback, useMemo } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  Button,
  HStack,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useTags } from "modules/providers/tags-provider";
import colors from "app/colors";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import { HASH_KEY, TAG_GROUP_THREADS_PATH } from "modules/utils/Constants";
import { MIXPANEL_EVENT_TYPE } from "modules/providers/mixpanel-provider/mixpanel-provider";
import ExpandCollapseAccordionButton from "./expand-collapse-accordion-button";
import useFilterForSelectedState from "./use-filter-for-selected-state";
import { TagFragment, SearchFilterType } from "gql/graphql";
import { FileBoxIcon, TagIcon } from "modules/icons";
import useSearchFilter from "modules/hooks/use-search-filter";
import useAppState from "modules/hooks/use-app-state";
import selectedTagGroupNavLinkProps from "./selected-tag-group-nav-link-props";
import Link from "modules/layouts/_shared/link";
import idsToIndexes from "modules/utils/ids-to-indexes";

const LeftNavInactiveTags = () => {
  const { leftNavInactiveParentTags } = useTags();
  const filterForSelectedState = useFilterForSelectedState();
  const { track } = useMixpanel();
  const { getUrl } = useSearchFilter();

  const {
    arrayValue: expandedInactiveParentTagIds,
    updateArray: setExpandedInactiveParentTagIds,
  } = useAppState({
    appStateType: "expandedInactiveParentTagIds",
  });

  const expandedParentTagIndexes = useMemo(
    () => idsToIndexes(expandedInactiveParentTagIds, leftNavInactiveParentTags),
    [expandedInactiveParentTagIds, leftNavInactiveParentTags],
  );

  const toggleParentTag = useCallback(
    (id: string, expandOnly: boolean) => () => {
      const _expandedInactiveParentTagIds = [...expandedInactiveParentTagIds];
      if (_expandedInactiveParentTagIds.includes(id)) {
        if (!expandOnly) {
          setExpandedInactiveParentTagIds(
            _expandedInactiveParentTagIds.filter((v) => v !== id),
          );
          return;
        }
      }
      setExpandedInactiveParentTagIds([...expandedInactiveParentTagIds, id]);
    },
    [expandedInactiveParentTagIds, setExpandedInactiveParentTagIds],
  );

  const onTagClicked = useCallback(
    (tag: TagFragment, canExpand: boolean) => () => {
      if (canExpand) {
        toggleParentTag(tag.id, true)();
      }

      track(MIXPANEL_EVENT_TYPE.LINK, {
        "Link Location": "Left Hand Nav",
        "Link Action": "Hashtag",
        "Link Name": `${HASH_KEY}${tag.displayName}`,
      });
    },
    [toggleParentTag, track],
  );

  if (leftNavInactiveParentTags.length === 0) {
    return null;
  }

  return (
    <Accordion allowMultiple w="full">
      <AccordionItem border="none">
        {({ isExpanded }) => (
          <>
            <h1>
              <HStack spacing={1} py={1}>
                <ExpandCollapseAccordionButton
                  isExpanded={isExpanded}
                  canExpand
                />
                <Button variant="link" w="full">
                  <HStack spacing={1} w="full">
                    <FileBoxIcon fill={colors.grayMain} />
                    <Text
                      flex={1}
                      textAlign="left"
                      color={colors.grayMain}
                      fontStyle="italic"
                      fontSize={14}
                    >
                      Archived Tags
                    </Text>
                  </HStack>
                </Button>
              </HStack>
            </h1>
            <AccordionPanel py={0} pr={0}>
              <Accordion allowMultiple index={expandedParentTagIndexes}>
                {leftNavInactiveParentTags.map((parentTag) => (
                  <AccordionItem key={parentTag.id} border="none">
                    {({ isExpanded }) => (
                      <>
                        <h1>
                          <HStack spacing={1}>
                            <ExpandCollapseAccordionButton
                              isExpanded={isExpanded}
                              canExpand={!!parentTag.childTags.length}
                              {...(!!parentTag.childTags.length && {
                                onClick: toggleParentTag(parentTag.id, false),
                              })}
                            />
                            <Link
                              href={getUrl(TAG_GROUP_THREADS_PATH, undefined, {
                                type: SearchFilterType.TagIdList,
                                objectIdListValue: [parentTag.id],
                              })}
                              onClick={onTagClicked(
                                parentTag,
                                !!parentTag.childTags.length,
                              )}
                              fontSize="sm"
                              w="full"
                              py={0.5}
                              color={colors.grayMain}
                              fontWeight={400}
                              {...(filterForSelectedState?.id ===
                                parentTag.id && selectedTagGroupNavLinkProps)}
                            >
                              <HStack spacing={1}>
                                <TagIcon fill={colors.grayPrimary} />
                                <Text flex={1} fontSize={14}>
                                  {parentTag.name}
                                </Text>
                              </HStack>
                            </Link>
                          </HStack>
                        </h1>
                        <AccordionPanel py={0} pl={6} pr={0}>
                          <Wrap
                            spacing={1}
                            py={parentTag.childTags.length ? 0.5 : 0}
                            pr={4} // keeps child tags away from parent tag unread counts
                          >
                            {parentTag.childTags.map((childTag) => (
                              <WrapItem key={childTag.id}>
                                <Link
                                  href={getUrl(
                                    TAG_GROUP_THREADS_PATH,
                                    undefined,
                                    {
                                      type: SearchFilterType.TagIdList,
                                      objectIdListValue: [childTag.id],
                                    },
                                  )}
                                  onClick={onTagClicked(childTag, false)}
                                  borderWidth="1px"
                                  color={colors.grayMain}
                                  borderColor={colors.grayMain}
                                  rounded="full"
                                  px={1}
                                  fontSize={10}
                                  {...(filterForSelectedState?.id ===
                                    childTag.id && {
                                    ...selectedTagGroupNavLinkProps,
                                    borderColor:
                                      selectedTagGroupNavLinkProps.color,
                                    fontWeight: 700,
                                    fontSize: 10,
                                    rounded: "full",
                                  })}
                                >
                                  {childTag.name}
                                </Link>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                ))}
              </Accordion>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

export default memo(LeftNavInactiveTags);
